import { useEffect, useRef, useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import type { CaptureStartupStage } from "../hooks/useLiveSession";

type ConsentDialogProps = {
  open: boolean;
  busy?: boolean;
  startupStage?: CaptureStartupStage;
  requiresCourtApproval: boolean;
  onConfirm: (confirmation: { courtApproval: boolean }) => void;
  onCancel: () => void;
  sessionNumber?: number;
  participantCount?: number;
  glossaryCount?: number;
};

/**
 * Final consent gate shown immediately before capture begins.
 *
 * The Details rail already records standing authority evidence; this is the
 * point-of-recording confirmation that everyone present has actually been
 * told, which is the obligation that bites in most jurisdictions. It is
 * deliberately a blocking step with an unticked box: consent must be an
 * affirmative act each time, never a remembered preference.
 */
export function ConsentDialog({
  open,
  busy = false,
  startupStage = "idle",
  requiresCourtApproval,
  onConfirm,
  onCancel,
  sessionNumber = 1,
  participantCount = 0,
  glossaryCount = 0
}: ConsentDialogProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const confirmBox = useRef<HTMLInputElement>(null);
  const [announced, setAnnounced] = useState(false);
  const [courtApproved, setCourtApproved] = useState(false);
  const startupMessage =
    startupStage === "requesting-permission"
      ? "Requesting microphone access…"
      : startupStage === "preparing-audio"
        ? "Preparing secure audio and source capture…"
        : startupStage === "provider-ready"
          ? "Live transcription is ready. Confirming acknowledged audio…"
          : "Preparing recording…";

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) {
      // showModal gives us the focus trap, backdrop, and Escape handling.
      if (typeof element.showModal === "function") element.showModal();
      else element.setAttribute("open", "");
      setAnnounced(false);
      setCourtApproved(false);
      confirmBox.current?.focus();
    }
    if (!open && element.open) {
      if (typeof element.close === "function") element.close();
      else element.removeAttribute("open");
    }
  }, [open]);

  const confirm = () => {
    onConfirm({ courtApproval: courtApproved });
  };

  return (
    <dialog
      ref={dialog}
      className="consent-dialog"
      aria-label="Confirm before recording"
      aria-labelledby="consent-title"
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <div className="consent-dialog__head">
        <ShieldCheck size={18} aria-hidden="true" />
        <h2 id="consent-title">
          Please confirm
        </h2>
        <button
          className="icon-button"
          type="button"
          onClick={onCancel}
          aria-label={
            busy
              ? "Cancel recording setup"
              : "Cancel and return without recording"
          }
        >
          <X size={17} aria-hidden="true" />
        </button>
      </div>

      <p className="consent-dialog__lede">
        Please ensure everyone present has been told this device will record
        audio and has consented before you continue. Also confirm your authority
        and that confidentiality restrictions have been checked.
      </p>
      <p className="consent-dialog__note">
        Your browser may ask separately for microphone access. Choose Allow;
        device access is separate from recording authority.
      </p>

      <dl className="consent-dialog__session">
        <div>
          <dt>Next source</dt>
          <dd>Session {sessionNumber}</dd>
        </div>
        <div>
          <dt>Inherited</dt>
          <dd>
            {participantCount} participant{participantCount === 1 ? "" : "s"} · {glossaryCount}{" "}
            glossary term{glossaryCount === 1 ? "" : "s"}
          </dd>
        </div>
      </dl>

      <label className="consent-dialog__confirm">
        <input
          ref={confirmBox}
          type="checkbox"
          aria-label="Confirm"
          checked={announced}
          onChange={(event) => setAnnounced(event.target.checked)}
          disabled={busy}
        />
        <span>
          <strong>Confirm</strong>{" "}
          I am authorised to record; required notice or consent is complete;
          confidentiality restrictions have been checked.
        </span>
      </label>

      {requiresCourtApproval && (
        <label className="consent-dialog__confirm consent-dialog__confirm--court">
          <input
            type="checkbox"
            checked={courtApproved}
            onChange={(event) => setCourtApproved(event.target.checked)}
            disabled={busy}
          />
          <span>Express court or tribunal recording approval is held.</span>
        </label>
      )}

      <div className="consent-dialog__actions">
        <button
          className="button button--text"
          type="button"
          onClick={onCancel}
        >
          {busy ? "Cancel setup" : "Cancel"}
        </button>
        <button
          className="button button--primary"
          type="button"
          onClick={confirm}
          disabled={!announced || (requiresCourtApproval && !courtApproved) || busy}
          aria-busy={busy}
          aria-label={busy ? "Preparing recording" : undefined}
        >
          {busy ? startupMessage : "Start recording"}
        </button>
      </div>

      {busy && (
        <p className="consent-dialog__note" aria-live="polite">
          Dicta shows Recording only after live transcription is ready and the server has acknowledged PCM.
        </p>
      )}
    </dialog>
  );
}
